(function() {
  'use strict';

  const MOBILE_BREAKPOINT = 750;
  const MOBILE_MODAL_EXTRA_SPACE = 48;
  const POPOVER_OFFSET = 16;

  const instances = new Map();

  const isMobile = () => window.innerWidth < MOBILE_BREAKPOINT;

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function getMediaInfo(mediaEl) {
    if (!mediaEl) return null;
    if (mediaEl.tagName === 'IMG') {
      const w = mediaEl.naturalWidth;
      const h = mediaEl.naturalHeight;
      if (!w || !h) return null;
      return { intrinsicWidth: w, intrinsicHeight: h };
    }
    if (mediaEl.tagName === 'VIDEO') {
      const w = mediaEl.videoWidth;
      const h = mediaEl.videoHeight;
      if (!w || !h) return null;
      return { intrinsicWidth: w, intrinsicHeight: h };
    }
    return null;
  }

  function computeObjectFitTransform({ containerWidth, containerHeight, intrinsicWidth, intrinsicHeight, objectFit }) {
    if (!containerWidth || !containerHeight || !intrinsicWidth || !intrinsicHeight) return null;

    const fit = objectFit || 'fill';
    if (fit === 'fill' || fit === 'none' || fit === 'scale-down') return null;

    const scaleX = containerWidth / intrinsicWidth;
    const scaleY = containerHeight / intrinsicHeight;
    const scale = fit === 'contain' ? Math.min(scaleX, scaleY) : Math.max(scaleX, scaleY); // cover default

    const renderedWidth = intrinsicWidth * scale;
    const renderedHeight = intrinsicHeight * scale;

    const offsetX = (renderedWidth - containerWidth) / 2;
    const offsetY = (renderedHeight - containerHeight) / 2;

    return { scale, offsetX, offsetY };
  }

  class MediaTextDots {
    constructor(root) {
      this.root = root;
      this.currentOpenContent = null;
      this.dots = Array.from(root.querySelectorAll('.rb-interactive-media__dot'));
      this.contentsById = new Map();
      this._resizeRaf = null;

      this._contentOrigins = new Map();
      this.portal = null;
      this.portalDots = null;

      this._anchor = null;
      this._anchorRaf = null;
      this._anchorScrollEls = [];
      this._anchorScrollHandler = null;
      this._anchorResizeHandler = null;
      this._onRootClick = null;
      this._onWindowResize = null;

      instances.set(root.getAttribute('data-section-id'), this);

      root.querySelectorAll('.rb-interactive-media__dot-content[data-dot-id]').forEach((el) => {
        const id = el.getAttribute('data-dot-id');
        if (id) this.contentsById.set(id, el);
      });

      this._onRootClick = (e) => this.handleDotClick(e);
      root.addEventListener('click', this._onRootClick);
      this.dots.forEach(dot => dot.classList.add('rb-interactive-media__dot--visible'));

      this.initCropAwarePositions();
    }

    query(selector) {
      return this.root.querySelector(selector);
    }

    resolveOverlayMedia() {
      const host = this.root.parentElement;
      if (!host) return null;

      const img = host.querySelector('img');
      if (img) return img;

      const video = host.querySelector('video');
      if (video) return video;

      return null;
    }

    updateCropAwareDotPositions() {
      const mediaEl = this.resolveOverlayMedia();
      if (!mediaEl) return;

      const mediaInfo = getMediaInfo(mediaEl);
      if (!mediaInfo) return;

      const containerRect = (this.root.parentElement || this.root).getBoundingClientRect();
      if (!containerRect.width || !containerRect.height) return;

      const computed = window.getComputedStyle(mediaEl);
      const objectFit = computed.objectFit || 'cover';
      const transform = computeObjectFitTransform({
        containerWidth: containerRect.width,
        containerHeight: containerRect.height,
        intrinsicWidth: mediaInfo.intrinsicWidth,
        intrinsicHeight: mediaInfo.intrinsicHeight,
        objectFit,
      });

      if (!transform) {
        const gridHelpers = this.root.querySelectorAll('.rb-interactive-media__grid-helper');
        if (gridHelpers.length) {
          gridHelpers.forEach(el => {
            el.style.left = '0';
            el.style.top = '0';
            el.style.right = '0';
            el.style.bottom = '0';
            el.style.width = '';
            el.style.height = '';
          });
        }

        this.dots.forEach(dot => {
          const rawX = parseFloat(dot.getAttribute('data-position-x'));
          const rawY = parseFloat(dot.getAttribute('data-position-y'));
          if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) return;
          dot.style.setProperty('--dot-position-x', `${clamp(rawX, 0, 100)}%`);
          dot.style.setProperty('--dot-position-y', `${clamp(rawY, 0, 100)}%`);
        });
        return;
      }

      // In design mode the grid helper should align to the rendered image (including crop offsets)
      const gridHelpers = this.root.querySelectorAll('.rb-interactive-media__grid-helper');
      if (gridHelpers.length) {
        const { scale, offsetX, offsetY } = transform;
        const renderedWidth = mediaInfo.intrinsicWidth * scale;
        const renderedHeight = mediaInfo.intrinsicHeight * scale;
        const left = -offsetX;
        const top = -offsetY;

        gridHelpers.forEach(el => {
          el.style.left = `${left}px`;
          el.style.top = `${top}px`;
          el.style.right = 'auto';
          el.style.bottom = 'auto';
          el.style.width = `${renderedWidth}px`;
          el.style.height = `${renderedHeight}px`;
        });
      }

      this.dots.forEach(dot => {
        const rawX = parseFloat(dot.getAttribute('data-position-x'));
        const rawY = parseFloat(dot.getAttribute('data-position-y'));
        if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) return;

        const fx = clamp(rawX / 100, 0, 1);
        const fy = clamp(rawY / 100, 0, 1);

        const { scale, offsetX, offsetY } = transform;
        const renderedX = (fx * mediaInfo.intrinsicWidth) * scale - offsetX;
        const renderedY = (fy * mediaInfo.intrinsicHeight) * scale - offsetY;

        const px = (renderedX / containerRect.width) * 100;
        const py = (renderedY / containerRect.height) * 100;

        dot.style.setProperty('--dot-position-x', `${px}%`);
        dot.style.setProperty('--dot-position-y', `${py}%`);
      });
    }

    initCropAwarePositions() {
      const mediaEl = this.resolveOverlayMedia();
      if (!mediaEl) return;

      const schedule = () => {
        if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
        this._resizeRaf = requestAnimationFrame(() => this.updateCropAwareDotPositions());
      };

      schedule();

      if (mediaEl.tagName === 'IMG') {
        if (mediaEl.complete) schedule();
        else mediaEl.addEventListener('load', schedule, { once: true });
      } else if (mediaEl.tagName === 'VIDEO') {
        if (mediaEl.readyState >= 2) schedule();
        else mediaEl.addEventListener('loadedmetadata', schedule, { once: true });
      }

      this._onWindowResize = schedule;
      window.addEventListener('resize', this._onWindowResize);
    }

    ensurePortal() {
      if (this.portal) return this.portal;
      const el = document.createElement('div');
      el.className = 'rb-interactive-media rb-media-text-dots__portal';
      el.style.position = 'fixed';
      el.style.top = '0';
      el.style.left = '0';
      el.style.right = '0';
      el.style.bottom = '0';
      el.style.pointerEvents = 'none';
      el.style.zIndex = '90';
      // Provide `.rb-interactive-media__dots` so nested CSS for dot-content applies
      const dots = document.createElement('div');
      dots.className = 'rb-interactive-media__dots';
      dots.style.position = 'absolute';
      dots.style.top = '0';
      dots.style.left = '0';
      dots.style.right = '0';
      dots.style.bottom = '0';
      dots.style.pointerEvents = 'none';
      el.appendChild(dots);
      document.body.appendChild(el);
      this.portal = el;
      this.portalDots = dots;
      return el;
    }

    moveContentToPortal(content) {
      if (this._contentOrigins.has(content)) return;
      this._contentOrigins.set(content, content.parentElement);
      const portal = this.ensurePortal();
      (this.portalDots || portal).appendChild(content);
    }

    restoreContentFromPortal(content) {
      const origin = this._contentOrigins.get(content);
      if (origin) {
        origin.appendChild(content);
        this._contentOrigins.delete(content);
      }
    }

    getScrollParents(el) {
      const parents = [];
      let node = el?.parentElement;
      while (node && node !== document.body) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const overflowX = style.overflowX;
        const canScrollY = (overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight;
        const canScrollX = (overflowX === 'auto' || overflowX === 'scroll') && node.scrollWidth > node.clientWidth;
        if (canScrollX || canScrollY) parents.push(node);
        node = node.parentElement;
      }
      parents.push(window);
      return parents;
    }

    positionAnchored(button, content) {
      const buttonRect = button.getBoundingClientRect();

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const offset = POPOVER_OFFSET;

      const insets = (() => {
        let top = 0;
        let bottom = 0;

        const header = document.getElementById('rb-header');
        if (header) {
          const r = header.getBoundingClientRect();
          if (r.bottom > 0 && r.top <= 0) top = Math.max(top, r.bottom);
        }

        const quickActions = document.querySelector('.rb-quickactions__wrapper.is-visible');
        if (quickActions) {
          const r = quickActions.getBoundingClientRect();
          if (r.top < vh && r.bottom >= vh) bottom = Math.max(bottom, vh - r.top);
        }

        return { top, bottom };
      })();

      // Measure without any modal transforms affecting rects
      const prev = {
        position: content.style.position,
        top: content.style.top,
        left: content.style.left,
        transform: content.style.transform,
        display: content.style.display,
        visibility: content.style.visibility,
      };

      content.style.position = 'fixed';
      content.style.top = '0';
      content.style.left = '0';
      content.style.transform = 'none';
      content.style.visibility = 'hidden';
      content.style.display = 'block';
      const contentRect = content.getBoundingClientRect();
      content.style.visibility = prev.visibility || '';
      content.style.display = prev.display || '';
      content.style.position = prev.position || '';
      content.style.top = prev.top || '';
      content.style.left = prev.left || '';
      content.style.transform = prev.transform || '';

      const cw = contentRect.width;
      const ch = contentRect.height;

      const spaceBelow = (vh - insets.bottom) - buttonRect.bottom;
      const spaceAbove = buttonRect.top - insets.top;
      const preferBelow = spaceBelow >= spaceAbove;
      const canFitBelow = spaceBelow >= (ch + offset);
      const canFitAbove = spaceAbove >= (ch + offset);

      let top;
      if ((preferBelow && canFitBelow) || (!canFitAbove && canFitBelow)) {
        top = buttonRect.bottom + offset;
      } else if (canFitAbove) {
        top = buttonRect.top - ch - offset;
      } else {
        const minTop = insets.top + offset;
        const maxTop = (vh - insets.bottom) - ch - offset;
        top = maxTop > minTop
          ? clamp((buttonRect.top + buttonRect.height / 2) - ch / 2, minTop, maxTop)
          : minTop;
      }

      let left = (buttonRect.left + buttonRect.width / 2) - cw / 2;
      left = clamp(left, offset, vw - cw - offset);

      content.style.position = 'absolute';
      content.style.top = `${top}px`;
      content.style.left = `${left}px`;
      content.style.transform = 'none';
    }

    scheduleAnchorUpdate() {
      if (!this._anchor) return;
      if (this._anchorRaf) cancelAnimationFrame(this._anchorRaf);
      this._anchorRaf = requestAnimationFrame(() => {
        if (!this._anchor) return;
        this.positionAnchored(this._anchor.button, this._anchor.content);
      });
    }

    startAnchoredPortal(button, content) {
      this._anchor = { button, content };
      this._anchorScrollEls = this.getScrollParents(button);
      this._anchorScrollHandler = () => this.scheduleAnchorUpdate();
      this._anchorResizeHandler = () => this.scheduleAnchorUpdate();
      this._anchorScrollEls.forEach(el => el.addEventListener?.('scroll', this._anchorScrollHandler, { passive: true }));
      window.addEventListener('resize', this._anchorResizeHandler);
      this.scheduleAnchorUpdate();
    }

    stopAnchoredPortal() {
      this._anchor = null;
      if (this._anchorRaf) cancelAnimationFrame(this._anchorRaf);
      this._anchorRaf = null;
      if (this._anchorScrollHandler) {
        this._anchorScrollEls.forEach(el => el.removeEventListener?.('scroll', this._anchorScrollHandler));
      }
      if (this._anchorResizeHandler) window.removeEventListener('resize', this._anchorResizeHandler);
      this._anchorScrollEls = [];
      this._anchorScrollHandler = null;
      this._anchorResizeHandler = null;
    }

    openContent(dotId) {
      const content = this.contentsById.get(dotId);
      const button = this.query(`[data-dot-id="${dotId}"].rb-interactive-media__dot`);
      if (!content || !button) return;

      this.closeCurrentContent();

      if (isMobile()) {
        content.classList.add('rb-interactive-media__dot-content--modal');
        this.moveContentToPortal(content);
        this.startAnchoredPortal(button, content);
        content.style.maxHeight = `${Math.max(240, window.innerHeight - MOBILE_MODAL_EXTRA_SPACE)}px`;
        content.style.zIndex = '91';
        content.style.pointerEvents = 'auto';
      } else {
        content.classList.add('rb-interactive-media__dot-content--popover');
        this.moveContentToPortal(content);
        this.startAnchoredPortal(button, content);
        content.style.zIndex = '91';
        content.style.pointerEvents = 'auto';
      }

      content.setAttribute('aria-hidden', 'false');
      this.currentOpenContent = content;
    }

    closeContentElement(content) {
      if (!content) return;

      content.classList.remove('rb-interactive-media__dot-content--modal', 'rb-interactive-media__dot-content--popover');
      content.setAttribute('aria-hidden', 'true');
      ['position', 'top', 'left', 'bottom', 'right', 'transform', 'maxHeight', 'zIndex', 'pointerEvents'].forEach(prop => content.style[prop] = '');

      this.stopAnchoredPortal();
      this.restoreContentFromPortal(content);

      if (this.currentOpenContent === content) this.currentOpenContent = null;
    }

    closeContent(dotId) {
      const current = this.currentOpenContent;
      if (current && current.getAttribute('data-dot-id') === dotId) {
        this.closeContentElement(current);
        return;
      }

      const content = this.contentsById.get(dotId);
      if (content) this.closeContentElement(content);
    }

    closeCurrentContent() {
      if (this.currentOpenContent) this.closeContentElement(this.currentOpenContent);
    }

    handleDotClick(e) {
      const button = e.target.closest('.rb-interactive-media__dot');
      if (!button || !this.root.contains(button) || !button.classList.contains('rb-interactive-media__dot--visible')) return;
      const dotId = button.getAttribute('data-dot-id');
      if (!dotId) return;
      const current = this.currentOpenContent;
      if (current && current.getAttribute('data-dot-id') === dotId && current.getAttribute('aria-hidden') === 'false') {
        this.closeContent(dotId);
        return;
      }
      if (this.contentsById.has(dotId)) this.openContent(dotId);
    }

    destroy() {
      this.closeCurrentContent();

      Array.from(this._contentOrigins.keys()).forEach((content) => this.restoreContentFromPortal(content));

      if (this._onRootClick) this.root.removeEventListener('click', this._onRootClick);
      this._onRootClick = null;

      if (this._onWindowResize) window.removeEventListener('resize', this._onWindowResize);
      this._onWindowResize = null;

      if (this.portal && this.portal.parentNode) this.portal.parentNode.removeChild(this.portal);
      this.portal = null;
      this.portalDots = null;

      this.contentsById.clear();
      this.dots = [];
    }
  }

  function handleOutsideClick(e) {
    const content = e.target.closest('.rb-interactive-media__dot-content');
    const button = e.target.closest('.rb-interactive-media__dot');

    // Clicking on a dot or inside an open card should not close anything.
    if (content || button) return;

    // Otherwise close any open card (works for portaled mobile cards too).
    instances.forEach(instance => instance.closeCurrentContent());
  }

  function init(root = document) {
    root.querySelectorAll('.rb-mediatext__interactive-dots.rb-interactive-media[data-section-id]').forEach(el => {
      const key = el.getAttribute('data-section-id');
      if (!key) return;
      const existing = instances.get(key);
      if (existing && existing.root === el && existing.root.isConnected) return;
      if (existing && (!existing.root || !existing.root.isConnected || existing.root !== el)) instances.delete(key);
      if (!instances.has(key)) new MediaTextDots(el);
    });
  }

  function boot() {
    init(document);
    document.addEventListener('pointerdown', handleOutsideClick, true);
    document.addEventListener('keydown', (e) => e.key === 'Escape' && instances.forEach(i => i.closeCurrentContent()));

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', (e) => init(e.target));
      document.addEventListener('shopify:section:reorder', (e) => init(e.target));
      document.addEventListener('shopify:block:select', (e) => init(e.target));
      document.addEventListener('shopify:block:deselect', (e) => init(e.target));
      document.addEventListener('shopify:section:unload', (e) => {
        const sectionId = e.detail?.sectionId;
        if (!sectionId) return;
        Array.from(instances.entries()).forEach(([key, instance]) => {
          if (key === sectionId || key.startsWith(`${sectionId}-`)) {
            instance.destroy();
            instances.delete(key);
          }
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();

